import type { ConsultantDetail } from '#shared/types';
import { Badge } from '#client/components/ui/Badge';
import { Card } from '#client/components/ui/Card';
import { DetailList } from '#client/components/ui/DetailList';
import { ResourceDetailPage } from '#client/components/ui/ResourceDetailPage';
import { formatDateTime, dash } from '#client/lib/format';
import { RESOURCE } from '#client/lib/routes';

const PATH = RESOURCE.consultants;

/**
 * Readable by every role, editable only by an admin.
 *
 * A technician entering results and a receptionist about to print one both have
 * a legitimate reason to check who a report will be signed by — which is why
 * `GET /consultants` is ungated on the server too.
 */
export function ConsultantDetailPage() {
  return (
    <ResourceDetailPage<ConsultantDetail>
      path={PATH}
      noun="consultant"
      editCapability="consultant.edit"
      deleteCapability="consultant.delete"
      title={(consultant) => consultant.name}
      subtitle={(consultant) => consultant.designation}
      deleteLabel={(consultant) => consultant.name}
    >
      {(consultant) => (
        <div className="flex flex-col gap-5">
          <Card>
            <DetailList
              items={[
                { label: 'Designation', value: consultant.designation },
                { label: 'Qualifications', value: consultant.qualifications, wide: true },
                {
                  label: 'Registration',
                  value: dash(consultant.registration_no),
                  mono: consultant.registration_no !== null,
                },
                {
                  label: 'Status',
                  value: consultant.is_active ? (
                    <Badge tone="success">Active</Badge>
                  ) : (
                    <Badge tone="neutral">Retired</Badge>
                  ),
                },
                { label: 'Added', value: formatDateTime(consultant.created_at) },
                { label: 'Last updated', value: formatDateTime(consultant.updated_at) },
              ]}
            />
          </Card>

          <Card title="Signature" subtitle="As it prints under this doctor's name">
            <div className="c-signature-field__preview">
              {consultant.signature_data_url ? (
                <img src={consultant.signature_data_url} alt={`${consultant.name} signature`} />
              ) : (
                <span className="c-signature-field__empty">
                  No signature on file — reports print a ruled line to sign by hand
                </span>
              )}
            </div>
          </Card>
        </div>
      )}
    </ResourceDetailPage>
  );
}
